import type { Driver, Org, User } from '@hero/schema';
import type { Business } from '../config/app-registry.config';
import type { ReferralEventProps } from '../posthog/referral-properties';
import type { DocChange, DocUpdate } from './doc-change';

/**
 * Shared event catalog. Key = event name. Value = payload, or `undefined`
 * if none.
 *
 * Used by both PostHog `capture()` and the Nest in-process bus
 * (`AppEventsService`). Destinations live in {@link EVENT_META}.
 *
 * Each row's JSDoc states **owner: backend | frontend**. Capture / emit
 * in that process only — never the same name on client and server.
 *
 * **One action, one event.** A row names something a person did
 * (`landing_signup_click`, `auth_sign_up`, `order_created`). Context
 * about *how* they got there — referral code, campaign, app, channel —
 * is metadata on that event ({@link ReferralEventProps}, client
 * super-properties, backend context properties), never a second event
 * fired at the same moment. That keeps one funnel per action across
 * landing, dashboard, apps and WhatsApp, and lets any of them be broken
 * down by `referral_code` without joining two names.
 */
export type Events = {
  /** owner: backend */
  funnel_started: { funnelBusiness: Business };
  /** owner: backend */
  funnel_step: {
    funnelBusiness: Business;
    step: string;
    completedCount: number;
    leadStatus: string;
  };
  /** owner: backend */
  funnel_status: { funnelBusiness: Business; from: string; to: string };
  /** owner: backend */
  funnel_opted_out: { funnelBusiness: Business };
  /** owner: backend */
  agent_engaged: { callerState?: string; returning?: boolean };
  /** owner: backend */
  agent_blocked_caller: undefined;
  /** owner: backend */
  subscribe_offer_sent: { funnelBusiness: Business; orderCount?: number };
  /** owner: backend */
  subscribe_offer_answered: {
    funnelBusiness: Business;
    answer: 'accepted' | 'declined' | 'unrelated';
  };
  /** owner: backend — after the order write (HTTP, AI, WhatsApp) */
  order_created: { orderId: string };
  /** owner: backend — after book succeeds */
  order_booked: { orderId: string };
  /** owner: frontend — tap only; not the DB write */
  order_publish_tapped: undefined;
  /** owner: frontend */
  order_photo_uploaded: undefined;
  /** owner: frontend */
  order_navigate: { source?: string; inaccurate?: boolean };
  /** owner: frontend */
  order_location_opened: undefined;
  /** owner: frontend */
  order_contact_called: { source?: string; who?: 'contact' | 'driver' | 'inaccurate_location' };
  /** owner: backend */
  auth_sign_in: undefined;
  /**
   * owner: backend — an account was created (dashboard, app, WhatsApp).
   * Carries the referral attribution that landed on the Org / User, when
   * any; the same values are `$set_once` on the Person. Empty object
   * when the signup was not referred.
   */
  auth_sign_up: ReferralEventProps;
  /**
   * owner: backend — an account's shape changed after signup
   * (`AccountOpsService`): type change, role change, org merge, a member
   * who also drives. `by` is who asked — staff in the admin panel or the
   * user through the agent.
   */
  account_ops_changed: {
    op: 'change_org_roles' | 'to_org_account' | 'to_driver_account' | 'merge_orgs' | 'set_member_driver';
    by: 'admin' | 'self';
    orgId?: string;
    userId?: string;
  };
  /**
   * owner: backend — a phone moved (`UserPhoneService.changeUserPhone`).
   * `company`: one org re-pointed its Driver row at the User for the new
   * number; `person`: the User itself moved numbers. No phones in the payload.
   */
  user_phone_changed: {
    scope: 'company' | 'person';
    outcome: 'relinked' | 'merged_driver' | 'moved';
    actor: 'self' | 'org_admin' | 'staff';
    orgId?: string;
    driverId?: string;
    userId: string;
  };
  /** owner: frontend */
  auth_sign_out: undefined;
  /** owner: frontend */
  language_changed: { lang: string };
  /** owner: frontend */
  contact_us_clicked: undefined;
  /** owner: frontend — tap to start payment */
  payment_pay_tapped: { source: 'order' | 'invoice' | 'all' };
  /** owner: frontend */
  payment_bank_transfer_tapped: { amount: number };
  /** owner: frontend */
  invoice_proof_uploaded: undefined;
  /** owner: frontend */
  landing_demo_submitted: { form_id: string };
  /** owner: frontend */
  landing_signin_click: { placement?: string };
  /** owner: frontend */
  landing_signup_click: { placement?: string };
  /** owner: frontend */
  landing_store_badge_click: { store?: string };
  /** owner: frontend */
  landing_whatsapp_cta_click: { placement?: string };
  /** owner: frontend */
  landing_smart_banner_click: { store?: string; surface?: string };
  /** owner: frontend */
  landing_smart_banner_dismiss: undefined;

  /**
   * owner: frontend — a referral link (`?r=CODE` / deep link) was
   * opened on a web / app / embedded surface. The only referral-named
   * event: it is the action itself. Everything downstream (the Sign up
   * click, the signup, the order) is its own action event carrying the
   * code as metadata through the `referral_code` / `referral_code_current`
   * super-properties — never a parallel `referral_*` event.
   *
   * `first_touch` is true when nothing was stored before; false when a
   * second (campaign / ongoing) code landed on a browser that already
   * carries an original code. The original stays the reward owner.
   */
  referral_captured: {
    /**
     * The stored **original** (first-touch) code — same value as the
     * `referral_code` super-property, so this event never overrides it.
     */
    referral_code: string;
    /** The code that was just captured from the URL / deep link. */
    referral_code_current: string;
    referral_business?: Business;
    /** `true` when `referral_code_current` became the original on this capture. */
    first_touch: boolean;
  };
  /**
   * owner: backend — a person was attributed to a referral for the
   * **first time** (lead created under a code, or an account signed up
   * with one and no earlier lead carried it). Fires once per person;
   * propagating the same stamp between a lead and its account, or an
   * admin re-link, only updates person properties. Answers "where did
   * this person come from" without joining on `auth_sign_up`.
   */
  referral_attributed: ReferralEventProps & {
    referral_code: string;
    referral_partner_id: string;
    referral_link_id: string;
    /** `lead` — CRM row (WhatsApp / demo form); `account` — Org / User at signup. */
    subject: 'lead' | 'account';
  };

  /**
   * owner: backend — a payment gateway webhook arrived (paid / failed /
   * refunded). Nest only. `gateway` is a payload field so a future
   * processor emits this same name.
   */
  billing_payment_received: { gateway: 'moyasar'; paymentId: string };
  /**
   * owner: backend — our ledger settled a successful subscription charge.
   * Nest only this pass (not a PostHog event yet).
   */
  billing_paid: {
    billingId: string;
    transactionId: string;
    orgId: string;
    amount: number;
    currency: string;
    source: string;
    paidAt: string;
    subscriptionId?: string;
  };
  /** owner: backend — driver assigned; Nest fan-out + product event */
  order_driver_assigned: { orderId: string; driverUserId: string; orderRef: string };
  /** owner: backend — assigned vehicle changed; Nest fan-out + product event */
  order_vehicle_changed: { orderId: string; driverUserId: string; orderRef: string };
  /**
   * owner: backend — a Driver row was added to an account. Nest only.
   * `ownerIds` are the accounts (Org / User) the row belongs to — the
   * funnel listener resolves the lead through them. Emitted by the
   * domain write, so the agent, the admin panel, and the dashboard all
   * move the funnel the same way.
   */
  fleet_driver_created: { driverId: string; ownerIds: string[] };
  /** owner: backend — a Vehicle row was added to an account. Nest only. */
  fleet_vehicle_created: { vehicleId: string; driverId?: string; ownerIds: string[] };
  /** owner: backend — Logisti (Bayan) filing was switched on for an account. Nest only. */
  waybill_filing_connected: { ownerIds: string[] };
  /** owner: backend — Bayan accepted a trip for an order. Nest only. */
  waybill_issued: { orderId: string; tripId: string; ownerIds: string[] };
  /**
   * owner: backend — a Driver document was updated. Nest only; emitted by
   * the DB layer (`events.updated` on the collection), so every write path
   * fires it. Listeners keep their own copies in sync (`Vehicle.driver.name`, …).
   */
  driver_updated: DocUpdate<Driver>;
  /** owner: backend — an Org document was updated. Nest only; DB layer. */
  org_updated: DocUpdate<Org>;
  /** owner: backend — a User document was updated. Nest only; DB layer. */
  user_updated: DocUpdate<User>;
};

export type EventName = keyof Events;

export type EventDest = 'analytics' | 'nest';
export type EventOwner = 'backend' | 'frontend';
export type EventMeta = { owner: EventOwner; dest: readonly EventDest[] };

const analyticsBackend = { owner: 'backend', dest: ['analytics'] } as const;
const analyticsFrontend = { owner: 'frontend', dest: ['analytics'] } as const;

/**
 * Routing for every catalog name. `satisfies Record<EventName, …>` so a
 * new `Events` row without destinations is a type error.
 *
 * - `analytics` — valid at `capture()`
 * - `nest` — valid at `AppEventsService.emit` / `emitAsync`
 * - both — Nest emit auto-captures; do not `capture()` again at the write site
 */
export const EVENT_META = {
  funnel_started: analyticsBackend,
  funnel_step: analyticsBackend,
  funnel_status: analyticsBackend,
  funnel_opted_out: analyticsBackend,
  agent_engaged: analyticsBackend,
  agent_blocked_caller: analyticsBackend,
  subscribe_offer_sent: analyticsBackend,
  subscribe_offer_answered: analyticsBackend,
  order_created: analyticsBackend,
  order_booked: analyticsBackend,
  order_publish_tapped: analyticsFrontend,
  order_photo_uploaded: analyticsFrontend,
  order_navigate: analyticsFrontend,
  order_location_opened: analyticsFrontend,
  order_contact_called: analyticsFrontend,
  auth_sign_in: analyticsBackend,
  auth_sign_up: analyticsBackend,
  account_ops_changed: analyticsBackend,
  user_phone_changed: analyticsBackend,
  auth_sign_out: analyticsFrontend,
  language_changed: analyticsFrontend,
  contact_us_clicked: analyticsFrontend,
  payment_pay_tapped: analyticsFrontend,
  payment_bank_transfer_tapped: analyticsFrontend,
  invoice_proof_uploaded: analyticsFrontend,
  landing_demo_submitted: analyticsFrontend,
  landing_signin_click: analyticsFrontend,
  landing_signup_click: analyticsFrontend,
  landing_store_badge_click: analyticsFrontend,
  landing_whatsapp_cta_click: analyticsFrontend,
  landing_smart_banner_click: analyticsFrontend,
  landing_smart_banner_dismiss: analyticsFrontend,
  referral_captured: analyticsFrontend,
  referral_attributed: analyticsBackend,
  billing_payment_received: { owner: 'backend', dest: ['nest'] },
  billing_paid: { owner: 'backend', dest: ['nest'] },
  order_driver_assigned: { owner: 'backend', dest: ['analytics', 'nest'] },
  order_vehicle_changed: { owner: 'backend', dest: ['analytics', 'nest'] },
  fleet_driver_created: { owner: 'backend', dest: ['nest'] },
  fleet_vehicle_created: { owner: 'backend', dest: ['nest'] },
  waybill_filing_connected: { owner: 'backend', dest: ['nest'] },
  waybill_issued: { owner: 'backend', dest: ['nest'] },
  driver_updated: { owner: 'backend', dest: ['nest'] },
  org_updated: { owner: 'backend', dest: ['nest'] },
  user_updated: { owner: 'backend', dest: ['nest'] },
} as const satisfies Record<EventName, EventMeta>;

export type NestEventName = {
  [K in EventName]: 'nest' extends (typeof EVENT_META)[K]['dest'][number] ? K : never;
}[EventName];

export type AnalyticsEventName = {
  [K in EventName]: 'analytics' extends (typeof EVENT_META)[K]['dest'][number] ? K : never;
}[EventName];

/** The names among `K` whose payload is a whole document (`DocCreate` / `DocUpdate` / `DocDelete`). */
export type DocPayloadEventName<E, K extends keyof E> = {
  [P in K]: E[P] extends DocChange<unknown> ? P : never;
}[K];

/**
 * A full document carries PII and must never reach PostHog: any
 * analytics-destination row with a `Doc*` payload fails here, naming
 * the offending event.
 */
const docPayloadsAreNestOnly: [DocPayloadEventName<Events, AnalyticsEventName>] extends [never]
  ? true
  : DocPayloadEventName<Events, AnalyticsEventName> = true;
void docPayloadsAreNestOnly;

export function eventHasDest(name: EventName, dest: EventDest): boolean {
  return (EVENT_META[name].dest as readonly EventDest[]).includes(dest);
}

/**
 * Wire names already live in PostHog insights. Catalog keys stay
 * `funnel_step`; the sink sends `funnel:step_completed`.
 */
export const LEGACY_WIRE_NAMES = {
  funnel_started: 'funnel:started',
  funnel_step: 'funnel:step_completed',
  funnel_opted_out: 'funnel:opted_out',
  funnel_status: 'funnel:status_changed',
  agent_engaged: 'agent:engaged',
  agent_blocked_caller: 'agent:blocked_caller',
  subscribe_offer_sent: 'subscription:offer_sent',
  subscribe_offer_answered: 'subscription:offer_answered',
} as const satisfies Partial<Record<EventName, string>>;

/** GTM `dataLayer` event strings — landing ads triggers keep the old names. */
export const EVENT_GTM_NAMES = {
  landing_demo_submitted: 'demo_request_submitted',
  landing_signin_click: 'dashboard_signin_click',
  landing_signup_click: 'dashboard_signup_click',
  landing_store_badge_click: 'store_badge_click',
  landing_whatsapp_cta_click: 'whatsapp_cta_click',
  landing_smart_banner_click: 'smart_banner_click',
  landing_smart_banner_dismiss: 'smart_banner_dismiss',
} as const satisfies Partial<Record<EventName, string>>;

export function wireEventName(event: EventName): string {
  return (LEGACY_WIRE_NAMES as Partial<Record<EventName, string>>)[event] ?? event;
}

export function gtmEventName(event: EventName): string | undefined {
  return (EVENT_GTM_NAMES as Partial<Record<EventName, string>>)[event];
}

/** @deprecated Use {@link EventName}. Kept so existing `HeroEvent` imports compile during the cutover. */
export type HeroEvent = EventName;
